import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { emptyMetrics, type AnalyticsFilter, type AnalyticsMetric, type InteractionRecord } from '@/shared/analytics/AnalyticsTypes';

const PROMPT_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'users', 'outputTokens', 'totalTokens', 'avgOutputTokens', 'avgConfidence', 'allocatedCostUsd'];
const PROMPT_RECORD_LIMIT = 250;

/**
 * Loads the raw prompts (and a rolled-up summary) behind an arbitrary filter
 * scope — the Use Cases Prompts tab (scoped to a category) and the person page
 * (scoped to a user, with the category applied client-side in the grid).
 */
@injectable()
export class UseCasePromptsViewModel extends BaseViewModel {
    records: InteractionRecord[] = [];
    totalMatched = 0;
    summary: Record<AnalyticsMetric, number> = emptyMetrics();
    loading = false;
    errorMessage: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(filters: AnalyticsFilter[]): Promise<void> {
        this.records = [];
        this.totalMatched = 0;
        this.summary = emptyMetrics();
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const [recordsResult, aggregate] = await Promise.all([
                this.gateway.records({
                    source: 'interactions',
                    filters,
                    orderBy: { column: 'timestamp', direction: 'desc' },
                    limit: PROMPT_RECORD_LIMIT,
                }),
                this.gateway.aggregate({ source: 'interactions', groupBy: [], metrics: PROMPT_METRICS, filters }),
            ]);
            this.records = recordsResult.records;
            this.totalMatched = recordsResult.totalMatched;
            this.summary = aggregate.totals;
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
